import { getToken } from './token';
const BASE_URL = process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api';

export function formatApiError(payload, status) {
  let detail = payload?.detail ?? payload?.message ?? payload?.error;
  if (Array.isArray(detail)) {
    detail = detail.map((item) => item?.msg || item?.message || '').filter(Boolean).join('\n');
  } else if (detail && typeof detail === 'object') {
    detail = detail.message || detail.msg || '';
  }
  const text = String(detail || '').trim();
  const isRawStatus = !text
    || /^internal server error$/i.test(text)
    || /^error$/i.test(text)
    || text === '500'
    || /^request failed$/i.test(text);

  if (isRawStatus) {
    if (status === 401) return 'Your session expired. Please log in again.';
    if (status === 403) return 'You do not have permission to do this.';
    if (status === 404) return 'The requested record was not found.';
    return 'Something went wrong. Please try again.';
  }
  return text;
}

export function notifyLowBalance(detail) {
  if (!detail || detail.code !== 'low_balance') return;
  window.dispatchEvent(new CustomEvent('recruitai:low-balance', { detail }));
}

function throwApiError(payload, status) {
  const detail = payload?.detail;
  if (status === 402 && detail && typeof detail === 'object') notifyLowBalance(detail);
  throw new Error(formatApiError(payload, status));
}

async function request(path, options = {}) {
  const token = getToken();
  const { headers: extraHeaders, ...rest } = options;
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...extraHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throwApiError(data, res.status);
  if (data?.low_balance) {
    notifyLowBalance({
      code: 'low_balance',
      message: 'Your company wallet is too low for AI. Add money, then try again.',
      balance_display: data.balance_display,
      minimum_display: data.minimum_display,
    });
  }
  return data;
}

// ===== JOBS =====
export const jobsApi = {
  list: () => request('/jobs/'),
  get: (id) => request(`/jobs/${id}`),
  create: (data) => request('/jobs/', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/jobs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/jobs/${id}`, { method: 'DELETE' }),
  restore: (id) => request(`/jobs/${id}/restore`, { method: 'POST' }),
  clone: (id) => request(`/jobs/${id}/clone`, { method: 'POST' }),
  aiGenerateDescription: (title, skills, employment_type) =>
    request(`/jobs/ai/generate-description?title=${encodeURIComponent(title)}&employment_type=${employment_type}`, {
      method: 'POST', body: JSON.stringify(skills),
    }),
  aiSuggestSkills: (title) => request(`/jobs/ai/suggest-skills?title=${encodeURIComponent(title)}`),
  // Reads an uploaded JD and returns values to fill the form. Suggestions
  // only — nothing is saved until the recruiter submits.
  aiImportDescription: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = getToken();
    return fetch(`${BASE_URL}/jobs/ai/import-description`, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throwApiError(data, r.status);
      return data;
    });
  },
  aiAnalyzeBias: (id) => request(`/jobs/${id}/ai/analyze-bias`, { method: 'POST' }),
  getEmbedCode: (id) => request(`/jobs/${id}/embed-code`),
  // One board for the whole company — not tied to any single job.
  getCompanyEmbedCode: () => request('/jobs/embed-code'),
  // Team lead only: hand a job to a recruiter in their team, or keep it (null).
  assign: (id, assignedToId) =>
    request(`/jobs/${id}/assign`, { method: 'PATCH', body: JSON.stringify({ assigned_to_id: assignedToId || null }) }),
};

// ===== CHAT AGENT =====
export const chatApi = {
  send: (messages, context = null) => request('/chat/', {
    method: 'POST',
    body: JSON.stringify({ messages, context }),
  }),
};

// ===== CANDIDATES =====
export const candidatesApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )).toString();
    return request(`/candidates/?${qs}`);
  },
  listRejected: (params = {}) => {
    const qs = new URLSearchParams({
      ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')),
      rejected: 'true',
    }).toString();
    return request(`/candidates/?${qs}`);
  },
  get: (id) => request(`/candidates/${id}`),
  uploadResumes: (jobId, files, source = 'Upload', autoScore = true) => {
    const formData = new FormData();
    formData.append('job_id', jobId);
    formData.append('source', source);
    formData.append('auto_score', autoScore);
    files.forEach(f => formData.append('files', f));
    const token = getToken();
    return fetch(`${BASE_URL}/candidates/upload`, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throwApiError(data, r.status);
      if (data?.low_balance) {
        notifyLowBalance({
          code: 'low_balance',
          message: 'Your company wallet is too low for AI. Add money, then try again.',
        });
      }
      return data;
    });
  },
  scoreResume: (id) => request(`/candidates/${id}/score-resume`, { method: 'POST' }),
  rescanResume: (id) => request(`/candidates/${id}/rescan-resume`, { method: 'POST' }),
  fetchResume: async (id, inline = false) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}/candidates/${id}/resume${inline ? '?inline=1' : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatApiError(err, res.status));
    }
    const blob = await res.blob();
    return { blob, type: res.headers.get('Content-Type') || blob.type };
  },
  uploadResumeFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = getToken();
    return fetch(`${BASE_URL}/candidates/${id}/resume`, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throwApiError(data, r.status);
      return data;
    });
  },
  updateContact: (id, { name, email, phone } = {}) =>
    request(`/candidates/${id}/contact`, {
      method: 'PATCH',
      body: JSON.stringify({ name, email, phone }),
    }),
  updateProfile: (id, payload) =>
    request(`/candidates/${id}/profile`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  bulkScore: (jobId, minScore = 35) => request(`/candidates/bulk-score/${jobId}?min_score=${minScore}`, { method: 'POST' }),
  updateDecision: (id, decision, notes) =>
    request(`/candidates/${id}/decision?decision=${encodeURIComponent(decision)}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`, { method: 'PATCH' }),
  // Manual score override (SOW 4.11)
  overrideScore: (id, score, notes) =>
    request(`/candidates/${id}/manual-score`, { method: 'PATCH', body: JSON.stringify({ score, notes }) }),
  // Archive (SOW 4.12)
  archive: (id, reason) =>
    request(`/candidates/${id}/archive`, { method: 'PATCH', body: JSON.stringify({ reason }) }),
  unarchive: (id) => request(`/candidates/${id}/unarchive`, { method: 'PATCH' }),
  bulkArchive: (jobId) => request(`/candidates/bulk-archive/${jobId}`, { method: 'POST' }),
  // Soft delete / restore
  softDelete: (id) => request(`/candidates/${id}`, { method: 'DELETE' }),
  restore: (id) => request(`/candidates/${id}/restore`, { method: 'PATCH' }),
  listDeleted: (params = {}) => {
    const qs = new URLSearchParams({
      ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')),
      show_deleted: 'true',
    }).toString();
    return request(`/candidates/?${qs}`);
  },
};

// ===== INTERVIEWS =====
export const interviewsApi = {
  start: (candidateId) =>
    request('/interviews/start', { method: 'POST', body: JSON.stringify({ candidate_id: candidateId }) }),
  respond: (candidateId, message) =>
    request(`/interviews/${candidateId}/respond`, { method: 'POST', body: JSON.stringify(message) }),
  complete: (candidateId) => request(`/interviews/${candidateId}/complete`, { method: 'POST' }),
  getTranscript: (candidateId) => request(`/interviews/${candidateId}/transcript`),
  getVideos: (candidateId) => request(`/interviews/${candidateId}/videos`),
  getProctoring: (candidateId) => request(`/interviews/${candidateId}/proctoring`),
  videoUrl: (candidateId, filename) => `${BASE_URL}/interviews/${candidateId}/video/${filename}`,
  deleteVideo: (candidateId, filename) =>
    request(`/interviews/${candidateId}/video/${encodeURIComponent(filename)}`, { method: 'DELETE' }),
  // Schedule (SOW 5.1 / 5.2)
  scheduleInterview: (candidateId, slot, durationMin = 30) =>
    request(`/interviews/${candidateId}/schedule`, {
      method: 'POST', body: JSON.stringify({ slot, duration_min: durationMin }),
    }),
  cancelInterview: (candidateId) =>
    request(`/interviews/${candidateId}/cancel`, { method: 'POST' }),
  // Self-schedule (SOW 5.3)
  getAvailableSlots: (candidateId) => request(`/interviews/${candidateId}/available-slots`),
  // Reminders (SOW 5.4)
  getPendingReminders: () => request('/interviews/pending-reminders'),
  sendReminder: (candidateId, type = '24h') =>
    request(`/interviews/${candidateId}/send-reminder?reminder_type=${type}`, { method: 'POST' }),
  // Second round scheduling (post AI interview)
  scheduleSecondRound: (candidateId, slot, meetingType, meetingLink = '', location = '') =>
    request(`/interviews/${candidateId}/schedule-second-round`, {
      method: 'POST',
      body: JSON.stringify({ slot, meeting_type: meetingType, meeting_link: meetingLink, location }),
    }),
  cancelSecondRound: (candidateId) =>
    request(`/interviews/${candidateId}/cancel-second-round`, { method: 'POST' }),
  updateSecondRoundOutcome: (candidateId, done, feedback = '') =>
    request(`/interviews/${candidateId}/second-round-outcome`, {
      method: 'PATCH',
      body: JSON.stringify({ done, feedback }),
    }),
  // Pipeline stage override
  setPipelineStage: (candidateId, stage) =>
    request(`/interviews/${candidateId}/pipeline-stage?stage=${encodeURIComponent(stage)}`, { method: 'PATCH' }),
};

// ===== SHORTLIST =====
function exportUrl(kind, jobId, status) {
  const params = new URLSearchParams({ status });
  const t = getToken();
  if (t) params.set('token', t);
  const path = jobId ? `/shortlist/export-${kind}/${jobId}` : `/shortlist/export-${kind}`;
  return `${BASE_URL}${path}?${params}`;
}

// ===== TEAM =====
export const teamApi = {
  // The signed-in person's reporting chain and direct reports, with each
  // report's whole branch, for the dashboard.
  myTeam: () => request('/admin/my-team'),
  // Active recruiters a team lead can assign a new job to (their own team).
  assignableRecruiters: () => request('/admin/users')
    .then(d => (d.users || []).filter(u => u.role === 'recruiter' && u.is_active)),
};

export const shortlistApi = {
  generate: (jobId, topN = 10, minScore = 50) =>
    request(`/shortlist/generate/${jobId}?top_n=${topN}&min_final_score=${minScore}`, { method: 'POST' }),
  get: (jobId, status = 'in_review') => request(`/shortlist/${jobId}?status=${status}`),
  getAll: (status = 'in_review') => request(`/shortlist/all?status=${status}`),
  compare: (jobId, ids) => request(`/shortlist/compare/${jobId}?ids=${ids.join(',')}`),
  // Export URLs (SOW 6.12 / 6.13) — no jobId exports every job; status picks the tab.
  // Token goes in the query because these open as plain downloads.
  exportCsvUrl: (jobId, status = 'in_review') => exportUrl('csv', jobId, status),
  exportExcelUrl: (jobId, status = 'in_review') => exportUrl('excel', jobId, status),
  exportPdfUrl: (jobId, status = 'in_review') => exportUrl('pdf', jobId, status),
  // Hiring manager notification (SOW 6.14)
  notifyManager: (jobId) => request(`/shortlist/notify-manager/${jobId}`, { method: 'POST' }),
};
